/**
 * /admin/twitter-drafts — admin-only review queue for tweet drafts.
 * ===================================================================
 * Lists pending drafts auto-generated from the Daily AI Brief + the
 * AI-inference price-drop generator. Founder/admin reviews each, clicks
 * "Tweet" (opens Twitter intent URL with text pre-filled) → status
 * flips to `tweeted`, or "Skip" → status flips to `skipped`.
 *
 * Why no automated posting? One bad tweet > zero tweets. Human-in-the-
 * loop guard rails matter more than 9-AM-Tuesday automation.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Twitter, Send, X, Sparkles, Loader2, Trash2, RefreshCcw,
  ChevronDown, AlertTriangle, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending:  'border-emerald-400/40 text-emerald-300',
  tweeted:  'border-emerald-400/40 text-cyan-300',
  skipped:  'border-slate-500/40 text-slate-400',
};

const TwitterDrafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [generating, setGenerating] = useState(false);

  // Quick-generate price-drop form
  const [pdProvider, setPdProvider] = useState('Anthropic');
  const [pdModel, setPdModel] = useState('Claude Haiku 4.5');
  const [pdOld, setPdOld] = useState('1.0000');
  const [pdNew, setPdNew] = useState('0.5000');

  // Manual draft compose
  const [manualText, setManualText] = useState('');
  const [creatingManual, setCreatingManual] = useState(false);

  const auth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const { data } = await axios.get(`${API_URL}/twitter-drafts/list`, {
        headers: auth(), params,
      });
      setDrafts(data.drafts || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) toast.error('Admin access required.');
      else toast.error(detail || 'Failed to load drafts');
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, auth]);

  useEffect(() => { load(); }, [load]);

  const handleTweet = async (d) => {
    window.open(d.intent_url, '_blank', 'noopener,noreferrer');
    try {
      await axios.patch(`${API_URL}/twitter-drafts/${d.id}`, { status: 'tweeted' }, { headers: auth() });
      toast.success('Marked as tweeted');
      await load();
    } catch { toast.error('Could not mark tweeted'); }
  };

  const handleSkip = async (d) => {
    try {
      await axios.patch(`${API_URL}/twitter-drafts/${d.id}`, { status: 'skipped' }, { headers: auth() });
      toast.success('Skipped');
      await load();
    } catch { toast.error('Could not skip'); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API_URL}/twitter-drafts/${d.id}`, { headers: auth() });
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const handleFromBrief = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/twitter-drafts/from-daily-brief`, {}, { headers: auth() });
      toast.success(`${data.created || 0} drafts created from today's Brief`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate from Brief');
    } finally { setGenerating(false); }
  };

  const handlePriceDrop = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/twitter-drafts/price-drop`, null, {
        headers: auth(),
        params: {
          provider: pdProvider,
          model: pdModel,
          old_price_per_1k: parseFloat(pdOld),
          new_price_per_1k: parseFloat(pdNew),
        },
      });
      toast.success('Price-drop draft created');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create price-drop draft');
    } finally { setGenerating(false); }
  };

  const handleCreateManual = async () => {
    const txt = manualText.trim();
    if (txt.length < 4) { toast.error('Tweet too short'); return; }
    setCreatingManual(true);
    try {
      await axios.post(`${API_URL}/twitter-drafts/create`, { text: txt }, { headers: auth() });
      setManualText('');
      toast.success('Draft saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    } finally { setCreatingManual(false); }
  };

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="twitter-drafts-page">
      <header>
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
          <Twitter className="w-3.5 h-3.5 text-emerald-300" />
          <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
            Founder review queue
          </span>
        </div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Twitter / X Drafts</h1>
        <p className="text-slate-400 text-sm mt-1.5 max-w-3xl">
          Auto-generated from the Daily AI Brief + AI-inference price-drop alerts. Review each draft,
          click <strong>Tweet</strong> to open the prefilled compose window, or skip. One bad tweet
          beats zero tweets — human-in-the-loop, always.
        </p>
      </header>

      {/* Generators */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Card className="bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-300" /> From today's Daily Brief
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs">
              Generates up to 3 share-ready lines from the most recent Daily AI Brief.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleFromBrief}
              disabled={generating}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              data-testid="from-daily-brief-btn"
            >
              {generating
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating</>
                : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate from Brief</>}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-cyan-300" /> AI inference price drop
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs">
              When OpenAI / Anthropic / DeepSeek cut prices — viral fodder.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input value={pdProvider} onChange={(e) => setPdProvider(e.target.value)} placeholder="Provider" className="bg-slate-950 border-white/10 text-slate-100" data-testid="pd-provider"/>
              <Input value={pdModel} onChange={(e) => setPdModel(e.target.value)} placeholder="Model" className="bg-slate-950 border-white/10 text-slate-100" data-testid="pd-model"/>
              <Input value={pdOld} onChange={(e) => setPdOld(e.target.value)} placeholder="Old $/1K" className="bg-slate-950 border-white/10 text-slate-100" data-testid="pd-old"/>
              <Input value={pdNew} onChange={(e) => setPdNew(e.target.value)} placeholder="New $/1K" className="bg-slate-950 border-white/10 text-slate-100" data-testid="pd-new"/>
            </div>
            <Button
              size="sm"
              onClick={handlePriceDrop}
              disabled={generating}
              className="bg-emerald-500 hover:bg-cyan-400 text-slate-950 font-semibold w-full"
              data-testid="price-drop-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Generate draft
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Manual compose */}
      <Card className="bg-slate-900/60 border-white/10">
        <CardHeader>
          <CardTitle className="text-sm text-white">Compose a draft</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="Type a tweet (≤ 280 chars)…"
            className="bg-slate-950 border-white/10 text-slate-100 min-h-[80px]"
            maxLength={280}
            data-testid="manual-tweet-input"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{manualText.length} / 280</span>
            <Button
              size="sm"
              onClick={handleCreateManual}
              disabled={creatingManual || manualText.trim().length < 4}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              data-testid="create-manual-draft-btn"
            >
              {creatingManual
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving</>
                : <>Save draft</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="status-filter">
        {['pending', 'tweeted', 'skipped', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === s
                ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                : 'bg-slate-900 text-slate-300 border-white/10 hover:border-emerald-500/40'
            }`}
            data-testid={`filter-${s}`}
          >{s}</button>
        ))}
        <Button size="sm" variant="ghost" onClick={load} className="text-slate-400 hover:text-white text-xs h-7" data-testid="refresh-list">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading drafts…
        </div>
      ) : drafts.length === 0 ? (
        <Card className="bg-slate-900/40 border-white/10">
          <CardContent className="p-10 text-center" data-testid="empty-drafts">
            <Twitter className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">
              {statusFilter === 'pending'
                ? 'No pending drafts. Generate from Brief or compose one above.'
                : `No ${statusFilter} drafts yet.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="drafts-list">
          {drafts.map((d) => (
            <Card key={d.id} className="bg-slate-900/60 border-white/10" data-testid={`draft-row-${d.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge variant="outline" className={`${STATUS_COLORS[d.status]} text-[11px] uppercase tracking-wider`}>
                        {d.status}
                      </Badge>
                      <Badge variant="outline" className="border-emerald-400/30 text-cyan-200 text-[11px]">
                        {d.source}
                      </Badge>
                      <span className="text-[11px] text-slate-400">
                        {d.created_at ? new Date(d.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <p className="text-slate-100 text-sm whitespace-pre-wrap break-words">{d.text}</p>
                  </div>
                </div>
                {d.status === 'pending' && (
                  <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                    <Button
                      size="sm"
                      onClick={() => handleTweet(d)}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                      data-testid={`tweet-btn-${d.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Tweet
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSkip(d)}
                      className="border-slate-500/40 text-slate-300 hover:bg-white/5"
                      data-testid={`skip-btn-${d.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" /> Skip
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(d)}
                      className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 ml-auto"
                      data-testid={`delete-btn-${d.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TwitterDrafts;
